import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import {
  browserStorageGetSessionItem,
  browserStorageRemoveSessionItem,
  browserStorageSetSessionItem,
} from '@/utils/browserStorage';

const STORAGE_KEY = 'platformConnectionAttempt';
const CALLBACK_ALIASES: Record<string, string> = {
  tiktok_creator: 'tiktok',
  pinterest_v5: 'pinterest',
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function storageKey(platform: string) {
  return `${STORAGE_KEY}:${CALLBACK_ALIASES[platform] ?? platform}`;
}

interface ConnectionAttempt {
  attempt_id: string;
  brand_id?: number;
  rum_session_id?: string;
}

export function getConnectionAttempt(platform: string): ConnectionAttempt | undefined {
  try {
    const saved = JSON.parse(String(browserStorageGetSessionItem(storageKey(platform))));
    if (typeof saved.attempt_id !== 'string' || !UUID.test(saved.attempt_id)) return undefined;
    return {
      attempt_id: saved.attempt_id,
      brand_id: Number.isSafeInteger(saved.brand_id) ? saved.brand_id : undefined,
      rum_session_id: UUID.test(saved.rum_session_id) ? saved.rum_session_id : undefined,
    };
  } catch {
    // Invalid JSON, null context and unavailable storage all mean no saved attempt.
  }
  return undefined;
}

export function startConnectionAttempt(
  platform: string,
  brandId?: number,
): ConnectionAttempt | undefined {
  try {
    const attempt = {
      attempt_id: crypto.randomUUID(),
      brand_id: brandId,
      rum_session_id: datadogRum.getInternalContext()?.session_id,
    };
    browserStorageSetSessionItem(storageKey(platform), JSON.stringify(attempt));
    return attempt;
  } catch {
    // Diagnostics must never prevent connecting when storage or the SDK is unavailable.
  }
  return undefined;
}

export function clearConnectionAttempt(platform: string) {
  try {
    browserStorageRemoveSessionItem(storageKey(platform));
  } catch {
    // Best effort, like the other diagnostic operations.
  }
}

export function connectionDiagnosticHeaders(platform: string): Record<string, string> {
  const attempt = getConnectionAttempt(platform);
  if (!attempt) return {};
  return {
    'X-Connection-Attempt-Id': attempt.attempt_id,
    ...(attempt.rum_session_id ? { 'X-Rum-Session-Id': attempt.rum_session_id } : {}),
  };
}

export function recordConnectionStage(
  platform: string,
  attempt: ConnectionAttempt | undefined,
  stage: string,
  outcome: 'started' | 'completed' | 'failed',
  details: { reason?: string; http_status_code?: number; displayed_message?: string } = {},
) {
  // Callers pass fixed UI copy / reason codes, never errors, URLs, state or response objects.
  const context = { ...attempt, platform, stage, outcome, ...details };
  try {
    if (datadogRum.getInitConfiguration()) {
      datadogRum.addAction('platform_connection', context);
    }
    if (datadogLogs.getInitConfiguration()) {
      datadogLogs.logger.info('Platform connection diagnostic', { connection: context });
    }
  } catch {
    // Telemetry failures must not change the OAuth flow.
  }
}

/** Classify the displayed error without copying provider-controlled messages into telemetry. */
export function connectionFailureDetails(error: unknown, genericMessage: string) {
  const response = (
    error as
      | { response?: { status?: number; data?: { errors?: { _schema?: unknown } } } }
      | null
      | undefined
  )?.response;
  if (!response) {
    return {
      reason: 'network_or_client_error',
      displayed_message: '[no HTTP response; no error message displayed]',
    };
  }
  return {
    http_status_code: response.status,
    reason: 'http_error',
    displayed_message: response.data?.errors?._schema
      ? '[server-provided connection error]'
      : genericMessage,
  };
}
